import { DndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { QuestionAPI } from "../../Api/Question";
import { QuizAPI } from "../../Api/Quiz";
import ContentContainer from "../../Components/Container/ContentContainer";
import SectionContainer from "../../Components/Container/SectionContainer";
import {
  DescriptionLight,
  LabelHeader,
  SmallButton,
} from "../../Components/CustomComponent";
import { QUIZ_STATUS, WEB_ROUTES } from "../../constants";
import { useAppContext } from "../../Context/AppContext";
import { formatDate, formatTime } from "../../Helper";
import { Toast } from "../../Hooks/useToast";
import EmptyContainer from "../CustomComponent/EmptyContainer";
import CustomInfiniteScroll from "../InfiniteScroller";
import ContentLoader from "../Loader/ContentLoader";
import DeleteModal from "../Modal/DeleteModal";
import InviteModal from "../Modal/InviteModal";
import ThemeModal from "../Modal/ThemeModal";
import styles from "./index.module.scss";
import InviteListModal from "../Modal/InviteListModal";

export default function QuizSchedule({
  isQuiz = false,
  status = undefined,
  data = [],
  setQuiz,
  isLoading,
  getRecords,
  pagination,
  page,
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectedQuiz, SelectQuiz } = useAppContext();
  const [deleteModal, setDeleteModal] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [viewInviteModal, setViewInviteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [modalData, setModalData] = useState("");
  const quizAPI = QuizAPI();
  const questionAPI = QuestionAPI();
  const location = useLocation();

  const quiz_id = useMemo(
    () => location.state?.quiz_id,
    [location.state?.quiz_id]
  );

  const handleNavigate = (route, data) => {
    navigate(`/${route}`, { state: data });
  };

  const [quizzes, setQuizzes] = useState([]);

  const [draggingId, setDraggingId] = useState(null);

  // useEffect(() => {
  //   setQuizzes(data);
  // }, [data]);

  useEffect(() => {
    if (!isLoading) {
      if (page > 1) {
        let temp = [...quizzes];
        temp.push(...data);
        setQuizzes(temp);
      } else {
        setQuizzes(data);
      }
    }
  }, [page, isLoading]);

  const moveQuiz = (fromIndex, toIndex) => {
    const updatedQuizzes = [...quizzes];
    const [movedQuiz] = updatedQuizzes.splice(fromIndex, 1);
    updatedQuizzes.splice(toIndex, 0, movedQuiz);
    setQuizzes(updatedQuizzes);
  };

  // const DraggableContentContainer = ({ children, index }) => {
  //   const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
  //     id: `quiz-${index}`,
  //     data: { index },
  //   });

  //   return (
  //     <div
  //       ref={setNodeRef}
  //       {...listeners}
  //       {...attributes}
  //       className={`${styles.quizItem} ${isDragging ? styles.dragging : ""}`}
  //     >
  //       {children}
  //     </div>
  //   );
  // };

  const DraggableContentContainer = ({ children, index }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `quiz-${index}`,
      data: { index },
    });

    return (
      <div
        ref={setNodeRef}
        {...attributes}
        className={`${styles.quizItem} ${isDragging ? styles.dragging : ""}`}
      >
        <ContentContainer bg="#1A2550" hasBorder={false} classes="p-0">
          <div {...listeners} className={``}>
            &nbsp;
          </div>
          {children}
          <div {...listeners} className={``}>
            &nbsp;
          </div>
        </ContentContainer>
      </div>
    );
  };

  const DroppableContainer = ({ children, index }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: `drop-${index}`,
    });

    return (
      <div
        ref={setNodeRef}
        className={`${styles.dropTarget} ${isOver ? styles.dropTargetActive : ""
          }`}
      >
        {children}
      </div>
    );
  };

  const deleteQuizMutation = useMutation({
    mutationFn: async (id) => {
      const response = await quizAPI.deleteQuiz(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["quizzes"]);
      setDeleteModal(false);
      setDeleteLoading(false);
    },
    onError: (error) => {
      console.error("Failed to delete quiz:", error);
      Toast(error.message, "error", false);
      setDeleteLoading(false);
    },
  });

  const statusMutation = useMutation({
    mutationFn: async (id, data) => {
      const response = await questionAPI.updateStatus(id, data);
      return response.data;
    },
    onSuccess: () => {
      Toast("Status updated successfully!", "success", false);
    },
    onError: (error) => {
      console.error("Failed to delete quiz:", error);
      Toast(error.message, "error", false);
    },
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: async (id) => {
      const response = await questionAPI.deleteQuestion(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["quizz", quiz_id, "question"], {
        refetchInactive: true,
        refetchActive: true,
      });
      setDeleteModal(false);
      setDeleteLoading(false);
      Toast("Question Deleted Succesfully", "success", false);
    },
    onError: (error) => {
      Toast(error.message, "error", false);
      setDeleteLoading(false);
    },
  });

  const handleDeleteQuiz = () => {
    setDeleteLoading(true);
    if (!isQuiz) {
      deleteQuizMutation.mutate(modalData?.id);
    } else {
      deleteQuestionMutation.mutate(modalData?.id);
    }
  };

  const handleQuizStatus = (data) => {
    statusMutation.mutateAsync(modalData?.id, data);
  };

  return (
    <>
      <DndContext
        onDragStart={(event) => {
          setDraggingId(event.active.id);
        }}
        onDragEnd={({ active, over }) => {
          if (active.id !== over.id) {
            const activeIndex = Number(active.id.split("-")[1]);
            const overIndex = Number(over.id.split("-")[1]);
            moveQuiz(activeIndex, overIndex);
          }
          setDraggingId(null);
        }}
      >
        <div id="scrollableDiv" className={styles.container}>
          <SectionContainer classes={` my-4 gap-3 mx-auto`}>
            {quizzes.length === 0 && !isLoading ? (
              <EmptyContainer
                text={!isQuiz ? "No Quizzes Found" : "No Questions Found"}
              />
            ) : (
              <CustomInfiniteScroll
                dataLength={quizzes?.length}
                // LoadingScreen={
                //   <div className="flex items-center justify-center">
                //     <Spinner />
                //   </div>
                // }
                getRecords={getRecords}
                hasMore={page < pagination?.total}
              // endMessage={
              //   quizzes?.length > pagination?.total_records ? (
              //     <p className="text-white w-100 text-center">
              //       no more records
              //     </p>
              //   ) : (
              //     <div className="flex items-center justify-center">
              //       <Spinner />
              //     </div>
              //   )
              // }
              >
                {quizzes?.map((item, index) => (
                  <DroppableContainer key={index} index={index}>
                    <DraggableContentContainer index={index}>
                      <ContentContainer
                        bg="#1A2550"
                        hasBorder={false}
                        classes="p-3 mb-3"
                      >
                        {isQuiz ? (
                          <section className="d-flex flex-row gap-3 align-items-center">
                            {!item.image_url ? <></> :
                              <div className={styles.imageContainer}>
                                <img
                                  src={item?.image_url}
                                  alt={"Quiz " + index}
                                  className={styles.image}
                                />
                              </div>
                            }
                            <LabelHeader
                              text={`${index + 1}. ${item.question}`}
                              classes="fs-6 tracking-wide ls-10"
                            />
                          </section>
                        ) : (
                          <section className="d-flex flex-row gap-3">
                            <div className={styles.imageContainer}>
                              <img
                                src={item?.cover_image_url}
                                alt={"Quiz " + index}
                                className={styles.image}
                              />
                            </div>
                            <div className="w-100 d-flex flex-row align-items-center justify-content-between">
                              <div className="d-flex flex-column gap-2">
                                <LabelHeader
                                  text={item.title}
                                  classes="fs-8 tracking-wide"
                                />
                              </div>
                              <div className="d-flex align-items-start h-100 mt-1">
                                <span className="badge bg-warning text-dark fs-9 rounded-pill fw-bold">
                                  {
                                    item.status === QUIZ_STATUS.COMPLETED
                                      ? "Quiz Over"
                                      : item.status === QUIZ_STATUS.ONGOING
                                        ? "On Going" :
                                        item.status === QUIZ_STATUS.UPCOMING
                                          ? "Upcoming"
                                          : "Not Started"
                                  }
                                </span>
                              </div>
                            </div>
                          </section>
                        )}
                        <section
                          className={`${styles.schedule} d-flex flex-row gap-2 mt-3`}
                        >
                          <div className="w-25 d-flex flex-row align-items-start justify-content-between">
                            <div className="d-flex flex-column gap-1">
                              <DescriptionLight
                                text={isQuiz ? "Type" : "Start At"}
                                classes="fs-9"
                              />
                              {!isQuiz ? (
                                <p className="fs-10 mb-0 text-white">
                                  {item?.start_at
                                    ? `${formatDate(
                                      item?.start_at
                                    )} ${formatTime(item?.start_at)}`
                                    : "-"}
                                </p>
                              ) : (
                                <p className="fs-10 mb-0 text-white">
                                  {item?.question_type}
                                </p>
                              )}
                            </div>
                          </div>
                          <hr className={styles.separator} />
                          <div className="w-25 d-flex flex-row align-items-start justify-content-between">
                            <div className="d-flex flex-column gap-1">
                              <DescriptionLight
                                text={isQuiz ? "Status" : "Pin"}
                                classes="fs-9"
                              />
                              <p className="fs-10 mb-0 text-white">
                                {isQuiz
                                  ? item?.status == 1
                                    ? "Active"
                                    : "Inactive"
                                  : item?.code}
                              </p>
                            </div>
                          </div>
                          <hr className={styles.separator} />
                          <div className="w-25 d-flex flex-row align-items-start justify-content-between">
                            <div className="d-flex flex-column gap-1">
                              <DescriptionLight
                                text={isQuiz ? "Options" : "Contestant"}
                                classes="fs-9"
                              />
                              <p className="fs-10 mb-0 text-white">
                                {isQuiz
                                  ? item?.options?.length || 0
                                  : item?.contestant_count}
                              </p>
                            </div>
                          </div>
                          <hr className={styles.separator} />
                          <div className="w-25 d-flex flex-row align-items-start justify-content-between">
                            <div className="d-flex flex-column gap-1">
                              <DescriptionLight
                                text={isQuiz ? "Duration" : "Audience"}
                                classes="fs-9"
                              />
                              <p className="fs-10 mb-0 text-white">
                                {isQuiz
                                  ? `${item?.duration} sec`
                                  : item?.audience_count}
                              </p>
                            </div>
                          </div>
                        </section>
                        {(isQuiz && (status === 20)) ? "" :
                          <section
                            className={`${styles.schedule} d-flex flex-row flex-wrap gap-2 mt-3 w-100 justify-content-center mb-2`}
                          >
                            {item.status === 10 && (
                              <SmallButton
                                text="Edit"
                                classes="mt-2"
                                color="rgba(204, 255, 0, 0.5)"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  SelectQuiz(item);
                                  if (isQuiz) {
                                    handleNavigate(WEB_ROUTES.UPDATE_QUESTION, {
                                      question: item,
                                    });
                                  } else {
                                    handleNavigate(
                                      WEB_ROUTES.EDIT_QUIZ(item.id),
                                      {
                                        quiz: item,
                                      }
                                    );
                                  }
                                }}
                              />
                            )}
                            {!isQuiz && item.status !== 20 && (
                              <SmallButton
                                text="Delete"
                                classes="mt-2"
                                color="rgba(237, 23, 61, 0.5)"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteModal(true);
                                  setModalData(item);
                                }}
                              />
                            )}
                            {item.status !== 20 && (
                              <SmallButton
                                text={`Clone ${isQuiz ? "Question" : "Quiz"}`}
                                classes="mt-2 w-50"
                                single={true}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  SelectQuiz(item);
                                  if (isQuiz) {
                                    handleNavigate(WEB_ROUTES.ADD_QUESTION, {
                                      quiz_id: item.quiz_id,
                                      question: item,
                                    });
                                  } else {
                                    const clone_quiz = item;
                                    delete clone_quiz.start_at;
                                    delete clone_quiz.end_at;
                                    handleNavigate(WEB_ROUTES.CREATE_QUIZ, {
                                      quiz: clone_quiz,
                                    });
                                  }
                                }}
                              />
                            )}

                            {!isQuiz && (
                              <>
                                {item.status !== 30 && item.status !== 40 && (
                                  <>
                                    <SmallButton
                                      text="Send Invite"
                                      classes="mt-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInviteModal(true);
                                        setModalData(item);
                                      }}
                                    />
                                    <SmallButton
                                      text="View Invite"
                                      classes="mt-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setModalData(item);
                                        setViewInviteModal(true);
                                      }}
                                    />
                                  </>
                                )}
                                {item.status === 10 && (
                                  <SmallButton
                                    text="Organize Quiz"
                                    classes="mt-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      SelectQuiz(item);
                                      handleNavigate(WEB_ROUTES.TUTORIAL_LOGIN, {
                                        pin: item?.code
                                      });
                                    }}
                                  />
                                )}
                                {item.status !== 40 && (
                                  <SmallButton
                                    text={
                                      item.status === QUIZ_STATUS.COMPLETED
                                        ? "View Result"
                                        : "Join Quiz"
                                    }
                                    classes="mt-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      SelectQuiz(item);
                                      handleNavigate(WEB_ROUTES.TUTORIAL_LOGIN, {
                                        pin: item?.code,
                                      });
                                    }}
                                  />
                                )}
                                <SmallButton
                                  text="Questions"
                                  classes="mt-2"
                                  color="rgba(0, 255, 61, 0.5)"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    SelectQuiz(item);
                                    handleNavigate(WEB_ROUTES.QUIZ_HISTORY, {
                                      quiz_id: item?.id,
                                      status: item.status,
                                    });
                                  }}
                                />
                              </>
                            )}
                            {isQuiz && (
                              <>
                                <SmallButton
                                  text="Edit"
                                  classes="mt-2"
                                  color="rgba(0, 255, 61, 0.5)"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigate(WEB_ROUTES.UPDATE_QUESTION, {
                                      quiz_id: item?.quiz_id,
                                      question: item,
                                    });
                                    // handleQuizStatus({ status: 1 });
                                  }}
                                />
                                <SmallButton
                                  text="Delete"
                                  classes="mt-2"
                                  color="rgba(237, 23, 61, 0.5)"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteModal(true);
                                    setModalData(item);
                                    // handleQuizStatus({ status: 0 });
                                  }}
                                />
                              </>
                            )}
                          </section>
                        }
                      </ContentContainer>
                    </DraggableContentContainer>
                  </DroppableContainer>
                ))}
              </CustomInfiniteScroll>
            )}
            {!isLoading ? "" : <ContentLoader />}
          </SectionContainer>
        </div>
      </DndContext>

      <ThemeModal
        size={"sm"}
        title={""}
        isOpen={deleteModal}
        setIsOpen={setDeleteModal}
      >
        <DeleteModal
          setIsOpen={setDeleteModal}
          // refetch={refetch}
          onRemoveClick={handleDeleteQuiz}
          isLoading={deleteLoading}
        />
      </ThemeModal>

      <ThemeModal
        size={"md"}
        title={""}
        isOpen={inviteModal}
        setIsOpen={setInviteModal}
      >
        <InviteModal
          setIsOpen={setInviteModal}
          modalData={modalData}
          // refetch={refetch}
          onRemoveClick={handleDeleteQuiz}
        // isLoading={deleteLoading}
        />
      </ThemeModal>
      <ThemeModal
        size={"md"}
        title={"Invited Users"}
        isOpen={viewInviteModal}
        setIsOpen={setViewInviteModal}
      >
        <InviteListModal
          setIsOpen={setViewInviteModal}
          modalData={modalData}
        />
      </ThemeModal>
    </>
  );
}
